import { useMutation, useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL, invoiceFetch as apiFetch } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { invoiceKeys } from '@/features/invoices/keys'
import { USE_MOCK_AP_INVOICES, mockInvoicePdfBlob } from '../mock'
import type { ApInvoiceAttachment } from '../view-models'

export function useUploadInvoicePdf(uuid?: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      file,
      correctedInvoice = false,
      invoiceUuid = uuid,
    }: {
      file: File
      correctedInvoice?: boolean
      invoiceUuid?: string
    }) => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          id: 9884,
          file_name: file.name,
          content_type: file.type,
          size_bytes: file.size,
        }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      const formData = new FormData()
      formData.append('file', file)
      formData.append('corrected_invoice', String(correctedInvoice))
      return apiFetch<ApInvoiceAttachment>(token, `/dashboard/ap-invoices/${invoiceUuid}/attachments`, {
        method: 'POST',
        body: formData,
      })
    },
    onSuccess: (_attachment, { invoiceUuid = uuid }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(invoiceUuid ?? ''),
      })
    },
  })
}

export async function downloadInvoiceAttachment(
  token: string,
  invoiceUuid: string,
  attachmentId: number,
): Promise<Blob> {
  if (USE_MOCK_AP_INVOICES) {
    return mockInvoicePdfBlob()
  }
  const response = await fetch(
    `${API_BASE_URL}/dashboard/ap-invoices/${invoiceUuid}/attachments/${attachmentId}/download`,
    { headers: { Authorization: 'Bearer ' + token } },
  )
  if (!response.ok) throw new Error(`Invoice PDF download failed with ${response.status}`)
  return response.blob()
}

/** A shipment's booking document: the BOL for freight, else the shipping label for parcel; null when neither is stored. */
export async function downloadShipmentBookingDocument(
  token: string,
  shipmentUuid: string,
): Promise<{ kind: 'bol' | 'label'; blob: Blob } | null> {
  for (const kind of ['bol', 'label'] as const) {
    const response = await fetch(`${API_BASE_URL}/dashboard/shipments/${shipmentUuid}/${kind}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (response.ok) return { kind, blob: await response.blob() }
    if (response.status !== 404) throw new Error('The booking document could not be loaded')
  }
  return null
}

export async function downloadInvoicePurchaseOrderPdf(
  token: string,
  purchaseOrderUuid: string,
  fileName: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/dashboard/purchase-orders/${purchaseOrderUuid}/pdf`,
    { headers: { Authorization: `Bearer ${token}` } },
  )
  if (!response.ok) throw new Error(`Download failed (${response.status})`)
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName
  link.click()
  URL.revokeObjectURL(objectUrl)
}

export async function downloadInvoiceMatchedDocument(
  token: string,
  downloadPath: string,
  fileName: string,
): Promise<void> {
  if (!downloadPath.startsWith('/dashboard/')) {
    throw new Error('Matched document has an invalid internal download path')
  }
  const response = await fetch(`${API_BASE_URL}${downloadPath}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) throw new Error(`Download failed (${response.status})`)
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName
  link.click()
  URL.revokeObjectURL(objectUrl)
}
